import { DashboardScreen } from "./components/DashboardScreen";
import { PotSelectorScreen } from "./components/PotSelectorScreen";
import { ResultScreen } from "./components/ResultScreen";
import { SetupScreen } from "./components/SetupScreen";
import { SpendTrackShell } from "./components/SpendTrackShell";
import { StorageConsentNotice } from "./components/StorageConsentNotice";
import { useSpendTrackState } from "./hooks/useSpendTrackState";
import type { SpendTrackAppSettings } from "./types";

export function SpendTrackApp({ settings }: { settings: SpendTrackAppSettings }) {
  const tracker = useSpendTrackState({ isAuthenticated: settings.isAuthenticated });

  return (
    <SpendTrackShell
      currencyPreference={tracker.currencyPreference}
      selectedCurrencyOption={tracker.selectedCurrencyOption}
      settings={settings}
      themeMode={tracker.themeMode}
      onCurrencyPreferenceChange={tracker.updateCurrencyPreference}
    >
      {tracker.screen === "setup" || (!tracker.configuration && tracker.screen !== "potSelector") ? (
        <SetupScreen
          currencyPreference={tracker.currencyPreference}
          existingConfiguration={tracker.configuration}
          formatter={tracker.formatter}
          maxMoneyAmount={settings.maxMoneyAmount}
          potName={tracker.currentPotName}
          selectedCurrencyOption={tracker.selectedCurrencyOption}
          setupDefaults={tracker.setupDefaults}
          themeMode={tracker.themeMode}
          onCurrencyPreferenceChange={tracker.updateCurrencyPreference}
          onOpenPotSelector={settings.isAuthenticated ? tracker.openPotSelector : undefined}
          onCancel={tracker.configuration ? tracker.returnToBudget : undefined}
          onStartTracking={tracker.startTracking}
          onToggleTheme={tracker.toggleTheme}
        />
      ) : null}

      {tracker.screen === "potSelector" ? (
        <PotSelectorScreen
          isAuthenticated={settings.isAuthenticated}
          selectedPotId={tracker.selectedPotId}
          spendingPots={tracker.spendingPots}
          statusMessage={tracker.potStatusMessage}
          restoreErrorMessage={tracker.restoreErrorMessage}
          onArchivePot={tracker.archiveSpendingPot}
          onCreatePot={tracker.createSpendingPot}
          onReturn={tracker.configuration ? tracker.returnToBudget : () => tracker.setScreen("setup")}
          onRestorePot={tracker.restoreSpendingPot}
          onSelectPot={tracker.selectSpendingPot}
        />
      ) : null}

      {tracker.screen === "dashboard" && tracker.configuration ? (
        <DashboardScreen
          configuration={tracker.configuration}
          currentDayNumber={tracker.currentDayNumber}
          formatter={tracker.formatter}
          maxMoneyAmount={settings.maxMoneyAmount}
          redoStackByDay={tracker.redoStackByDay}
          setCurrentDayNumber={tracker.setCurrentDayNumber}
          setRedoStackByDay={tracker.setRedoStackByDay}
          setSpendAmountText={tracker.setSpendAmountText}
          setSpendByDay={tracker.setSpendByDay}
          setUndoStackByDay={tracker.setUndoStackByDay}
          showDayControls={settings.showDayControls}
          spendAmountText={tracker.spendAmountText}
          spendByDay={tracker.spendByDay}
          undoStackByDay={tracker.undoStackByDay}
          onFinishPeriod={() => tracker.setScreen("result")}
          onAddSpend={tracker.isServerTrackingMode ? tracker.addServerSpend : undefined}
          onOpenSetup={tracker.openSetup}
          onOpenPotSelector={settings.isAuthenticated ? tracker.openPotSelector : undefined}
          potName={tracker.currentPotName}
          onRedoSpend={tracker.isServerTrackingMode ? tracker.redoServerSpend : undefined}
          onUndoSpend={tracker.isServerTrackingMode ? tracker.undoServerSpend : undefined}
        />
      ) : null}

      {tracker.screen === "result" && tracker.configuration ? (
        <ResultScreen
          configuration={tracker.configuration}
          formatter={tracker.formatter}
          spendByDay={tracker.spendByDay}
          onStartNewPeriod={tracker.resetTracking}
        />
      ) : null}

      {!tracker.storageConsent ? <StorageConsentNotice onAccept={tracker.acceptStorageConsent} /> : null}
    </SpendTrackShell>
  );
}
